import { useMemo } from 'react';
import { useModuleConfig } from './queries/admin/useModuleConfig';
import type { IemModuleConfig } from '../types/moduleConfig';

/** Shape of the restricted issue-out program label bag. */
export interface RestrictedProgramLabels {
  menuLabel: string;
  programLabel: string;
  reimbursementTotalLabel: string;
  excludeReportLabel: string;
  accessNoticeSuffix: string;
  quantityControlsAriaLabel: string;
  removeItemAriaLabel: string;
}

const FALLBACK: RestrictedProgramLabels = {
  menuLabel: 'Restricted Issue Out',
  programLabel: 'Restricted Buy Program',
  reimbursementTotalLabel: 'Restricted Reimbursement Total',
  excludeReportLabel: 'Exclude Restricted',
  accessNoticeSuffix: 'Restricted Buy Program items only.',
  quantityControlsAriaLabel: 'Restricted Buy Program quantity controls',
  removeItemAriaLabel: 'Remove restricted buy program item',
};

/**
 * Provides the configurable restricted issue-out program labels from module
 * configuration (e.g. USASOC's "SOC Buy Program").
 */
export function useRestrictedProgramLabels(): RestrictedProgramLabels {
  const { config } = useModuleConfig<IemModuleConfig>('iem');
  const restrictedProgram = config.features.restricted_program;

  return useMemo<RestrictedProgramLabels>(
    () => ({
      menuLabel: restrictedProgram?.menu_label || FALLBACK.menuLabel,
      programLabel: restrictedProgram?.program_label || FALLBACK.programLabel,
      reimbursementTotalLabel:
        restrictedProgram?.reimbursement_total_label || FALLBACK.reimbursementTotalLabel,
      excludeReportLabel: restrictedProgram?.exclude_report_label || FALLBACK.excludeReportLabel,
      accessNoticeSuffix: restrictedProgram?.access_notice_suffix || FALLBACK.accessNoticeSuffix,
      quantityControlsAriaLabel:
        restrictedProgram?.quantity_controls_aria_label || FALLBACK.quantityControlsAriaLabel,
      removeItemAriaLabel:
        restrictedProgram?.remove_item_aria_label || FALLBACK.removeItemAriaLabel,
    }),
    [restrictedProgram],
  );
}
